/**
 * The star rating control on a recipe page.
 *
 * The stars themselves are already rendered by `snippets/recipe-rating.liquid`, from the metafield,
 * before this file runs. That matters: if this script never loads, or fails, the reader still sees
 * the rating. Everything here is the part that lets them *add* one.
 *
 * ## Where the vote goes
 *
 * `/apps/recipes/rate`, on the shop's own domain. Shopify forwards it to the admin app and appends
 * the signed id of whoever is logged in — see `src/lib/shopify/app-proxy.ts`. Nothing here sends a
 * customer id, because nothing here is trusted to know one: the page could be edited by the person
 * sitting in front of it.
 *
 * ## Why it asks the server even when Liquid says signed out
 *
 * It does not. `data-signed-in` comes from Liquid, which knows, so an anonymous shopper gets the
 * prompt immediately instead of waiting for a round trip to be told. The server checks again
 * regardless, and its answer is the one that counts — this is a courtesy, not a gate.
 */

const ENDPOINT = '/apps/recipes/rate';

class RecipeRating extends HTMLElement {
  connectedCallback() {
    this.handle = this.dataset.handle;
    this.signedIn = this.dataset.signedIn === 'true';
    this.loginUrl = this.dataset.loginUrl || '/account/login';

    this.valueEl = this.querySelector('.uxf-rating__value');
    this.countEl = this.querySelector('.uxf-rating__count');
    this.fillEl = this.querySelector('.uxf-rating__full');
    this.statusEl = this.querySelector('.uxf-rating__status');

    for (const button of this.querySelectorAll('.uxf-rating__button')) {
      button.addEventListener('click', () => this.rate(Number(button.dataset.value)));
    }
  }

  async rate(value) {
    if (!this.signedIn) return this.askToSignIn();

    this.dataset.busy = 'true';
    this.say('');

    try {
      const response = await fetch(`${ENDPOINT}?handle=${encodeURIComponent(this.handle)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // Same origin, so the session cookie Shopify needs to identify the shopper goes along
        // without being asked for.
        credentials: 'same-origin',
        body: JSON.stringify({ handle: this.handle, value }),
      });

      const result = await response.json().catch(() => ({}));

      if (response.status === 401) {
        // Signed out after all — the page was loaded before the session expired.
        this.signedIn = false;
        if (result.loginUrl) this.loginUrl = result.loginUrl;
        return this.askToSignIn();
      }
      if (!response.ok) throw new Error(result.error || 'That did not save.');

      this.render(result.ratingValue, result.ratingCount);
      this.say(`Thanks — you rated this ${value} out of 5.`);
    } catch (error) {
      // Never silently. A star that appears to register and did not is worse than an error.
      this.say(error.message || 'That did not save. Try again in a moment.');
    } finally {
      this.dataset.busy = 'false';
    }
  }

  render(value, count) {
    if (this.fillEl) this.fillEl.style.width = `${(Number(value) / 5) * 100}%`;
    if (this.valueEl) this.valueEl.textContent = Number(value).toFixed(1);
    if (this.countEl) this.countEl.textContent = `(${count})`;
  }

  say(message) {
    if (this.statusEl) this.statusEl.textContent = message;
  }

  askToSignIn() {
    const modal = document.createElement('div');
    modal.className = 'uxf-rating-modal';
    modal.innerHTML = `
      <div class="uxf-rating-modal__card" role="dialog" aria-modal="true" aria-labelledby="uxf-rating-modal-title">
        <h2 id="uxf-rating-modal-title">Sign in to rate</h2>
        <p>Ratings are tied to your account, so each person counts once and you can change your mind later.</p>
        <div class="uxf-rating-modal__actions">
          <a class="uxf-rating-modal__primary" href="${this.loginUrl}">Sign in</a>
          <button type="button" class="uxf-rating-modal__secondary">Not now</button>
        </div>
      </div>`;

    const close = () => {
      modal.remove();
      document.removeEventListener('keydown', onKey);
    };
    const onKey = (event) => {
      if (event.key === 'Escape') close();
    };

    // Clicking the backdrop closes; clicking the card does not.
    modal.addEventListener('click', (event) => {
      if (event.target === modal) close();
    });
    modal.querySelector('.uxf-rating-modal__secondary').addEventListener('click', close);
    document.addEventListener('keydown', onKey);

    document.body.appendChild(modal);
    modal.querySelector('.uxf-rating-modal__primary').focus();
  }
}

if (!customElements.get('recipe-rating')) {
  customElements.define('recipe-rating', RecipeRating);
}
